'use client';

import { useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { type BaseComponentProps } from '@bedrock/core/contentful';
import { Button } from '@bedrock/ui/button';
import { Select } from '@bedrock/ui/select';
import { TextInput } from '@bedrock/ui/text-input';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ContentfulFormParsedContent } from 'lib/contentful/types';
import { analytics } from 'ui/analytics';
import { LazyWrap } from 'ui/common/lazy-wrap';
import { CheckboxField, DatePicker, FormField, FormLayout } from 'ui/components';
import { handleCreateProfile } from 'ui/mulesoft';
import ReCAPTCHA, { useRecaptcha } from 'ui/recaptcha-v3';
import { getFieldByType, getLabel } from 'ui/utils/form';
import { type SubscribeSchema, subscribeSchema } from './schema';

const NEWSLETTER_FORM_TYPE = 'Newsletter';
const NEWSLETTER_OPTIN = 'Guinness';
const NEWSLETTER_OPTIN_TYPE = 'Guinness_Diageo';

function SubscribeForm({
  fields,
  states,
  dictionary,
}: Readonly<
  BaseComponentProps<ContentfulFormParsedContent> & {
    states: { label: string; value: string }[];
  }
>) {
  const { recaptchaToken, recaptchaRef, onVerify, onExpire, resetRecaptcha } = useRecaptcha();
  const formFields = fields.fields;
  const firstNameField = getFieldByType(formFields, 'First Name');
  const lastNameField = getFieldByType(formFields, 'Last Name');
  const dateOfBirthField = getFieldByType(formFields, 'Date of Birth');
  const emailField = getFieldByType(formFields, 'Email');
  const zipCodeField = getFieldByType(formFields, 'Zip Code');
  const stateField = getFieldByType(formFields, 'State');
  const termsField = getFieldByType(formFields, 'Terms');

  const { register, handleSubmit, setValue, trigger, reset, formState, control } =
    useForm<SubscribeSchema>({
      resolver: zodResolver(subscribeSchema),
      mode: 'onBlur',
    });

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setValue('dateOfBirth', selectedDate, { shouldValidate: true });
    }
  };

  const [submitRequestStatus, setSubmitRequestStatus] = useState<'idle' | 'success' | 'error'>(
    'idle',
  );
  const [submitRequestError, setSubmitRequestError] = useState('');

  const handleOverlayClose = () => {
    setSubmitRequestStatus('idle');
  };

  const onSuccess: SubmitHandler<SubscribeSchema> = async (data) => {
    try {
      const result = await handleCreateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth,
        email: data.email,
        zipCode: data.zipCode,
        province: data.state,
        marketing: data.terms,
        hasAcceptedTerms: data.terms,
        recaptchaToken: recaptchaToken ?? '',
      });

      if (result.status === 200) {
        setSubmitRequestStatus('success');
        analytics.newsletterSignup({
          form_type: NEWSLETTER_FORM_TYPE,
          newsletter_email_optin: NEWSLETTER_OPTIN,
          newsletter_optin_type: NEWSLETTER_OPTIN_TYPE,
          status: 'Success',
        });
        reset();
        resetRecaptcha();
      } else {
        setSubmitRequestStatus('error');
        setSubmitRequestError(result.message ?? '');
        analytics.newsletterSignup({
          form_type: NEWSLETTER_FORM_TYPE,
          newsletter_email_optin: NEWSLETTER_OPTIN,
          newsletter_optin_type: NEWSLETTER_OPTIN_TYPE,
          status: 'Failed',
        });
      }
    } catch {
      setSubmitRequestStatus('error');
      setSubmitRequestError('Failed to subscribe');
      analytics.newsletterSignup({
        form_type: NEWSLETTER_FORM_TYPE,
        newsletter_email_optin: NEWSLETTER_OPTIN,
        newsletter_optin_type: NEWSLETTER_OPTIN_TYPE,
        status: 'Failed',
      });
    }
  };

  const dateOfBirth = useWatch({
    control,
    name: 'dateOfBirth',
  });

  return (
    <FormLayout
      onOverlayClose={handleOverlayClose}
      onSubmit={handleSubmit(onSuccess)}
      requiredFieldNote={dictionary.requiredFieldNote}
      status={submitRequestStatus}
      footerNote={
        dictionary.privacyNote ??
        'Guinness values and respects your privacy. You can unsubscribe at any time.'
      }
      overlayMessages={{
        closeLabel: dictionary.close,
        errorMessage: submitRequestError,
        errorTitle: dictionary.errorTitle,
        successMessage: dictionary.successMessage || 'Thank you for signing up for our newsletter',
        successTitle: dictionary.successTitle,
      }}
    >
      <div className="flex w-full flex-col gap-[12px]">
        <div className="flex flex-col gap-[12px] md:flex-row">
          <FormField error={formState.errors.firstName} label={getLabel(firstNameField)}>
            <TextInput
              className="h-[0px] border-0 !outline-none !ring-offset-0"
              {...register('firstName')}
              isValid={!formState.errors.firstName}
              maxLength={50}
              placeholder={firstNameField?.placeholder}
            />
          </FormField>
          <FormField error={formState.errors.lastName} label={getLabel(lastNameField)}>
            <TextInput
              className="border-0 !outline-none !ring-offset-0"
              {...register('lastName')}
              isValid={!formState.errors.lastName}
              maxLength={50}
              placeholder={lastNameField?.placeholder}
            />
          </FormField>
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <FormField error={formState.errors.dateOfBirth} label={getLabel(dateOfBirthField)}>
            <DatePicker
              date={dateOfBirth}
              error={formState.errors.dateOfBirth}
              onBlur={() => trigger('dateOfBirth')}
              onDateSelect={handleDateSelect}
              placeholder={dateOfBirthField?.placeholder ?? dictionary.selectDate ?? 'Select date'}
            />
          </FormField>
          <FormField error={formState.errors.email} label={getLabel(emailField)}>
            <TextInput
              className="border-0 !outline-none !ring-offset-0"
              {...register('email')}
              isValid={!formState.errors.email}
              maxLength={254}
              placeholder={emailField?.placeholder}
            />
          </FormField>
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <FormField error={formState.errors.state} label={getLabel(stateField)}>
            <Select
              className="border-0 !outline-none !ring-offset-0"
              {...register('state')}
              isValid={!formState.errors.state}
            >
              <option value="">{stateField?.placeholder ?? ''}</option>
              {states.map(({ label, value }) => (
                <option key={`${value}-${label}`} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField error={formState.errors.zipCode} label={getLabel(zipCodeField)}>
            <TextInput
              className="border-0 !outline-none !ring-offset-0"
              {...register('zipCode')}
              inputMode="numeric"
              isValid={!formState.errors.zipCode}
              maxLength={5}
              placeholder={zipCodeField?.placeholder}
              onInput={(event) => {
                if (event.currentTarget instanceof HTMLInputElement) {
                  event.currentTarget.value = event.currentTarget.value.replaceAll(/\D/g, '');
                }
              }}
            />
          </FormField>
        </div>
      </div>
      <CheckboxField
        error={formState.errors.terms}
        id="terms"
        label={termsField?.label}
        register={register('terms')}
      />
      <LazyWrap>
        <ReCAPTCHA ref={recaptchaRef} onExpire={onExpire} onVerify={onVerify} />
      </LazyWrap>
      <Button
        className="w-full md:max-w-[182px]"
        disabled={formState.isSubmitting}
        size="lg"
        variant="light"
      >
        {formState.isSubmitting
          ? (dictionary.submitting ?? 'Submitting...')
          : (dictionary.submit ?? 'Subscribe')}
      </Button>
    </FormLayout>
  );
}

export default SubscribeForm;
